import { useMemo, useState } from 'react';
import { AdminScreen, LoadOrError, adminErrorText, useResource } from './Overview';
import { Alert, Button, Card, EmptyState, Input, Select, Spinner, StatusPill, Textarea, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { cycleLabel, dateTimePt } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

type Cycle = 'monthly' | 'semiannual' | 'yearly';

interface LicenseCode {
  id: string;
  code: string;
  planKey: string;
  planName: string | null;
  billingCycle: string;
  note: string | null;
  createdAt: string;
  redeemedAt: string | null;
  revokedAt: string | null;
  redeemedByName: string | null;
  redeemedByEmail: string | null;
  redeemedBusinessId: string | null;
  redeemedBusinessName: string | null;
}

interface PlanPrice {
  key: string;
  name: string;
  monthlyCents: number;
  semiannualCents: number;
  yearlyCents: number;
}

interface Payload {
  codes: LicenseCode[];
  plans: PlanPrice[];
}

type Filter = 'all' | 'available' | 'used';

function statusOf(item: LicenseCode): { tone: 'neutral' | 'positive' | 'warning' | 'danger'; label: string } {
  if (item.revokedAt) return { tone: 'danger', label: tr('Cancelado') };
  if (item.redeemedAt) return { tone: 'positive', label: tr('Ativado') };
  return { tone: 'neutral', label: tr('Disponível') };
}

export function AdminLicensesPage(): JSX.Element {
  usePageMeta(
    tr('Licenças — administração'),
    tr('Gere códigos de ativação de planos e acompanhe quem já resgatou.'),
    '/admin/licencas'
  );

  const toast = useToast();
  const list = useResource<Payload>(() => api.get<Payload>('/api/admin/license-codes'));

  const plans = list.data?.plans ?? [];
  const codes = useMemo(() => list.data?.codes ?? [], [list.data]);

  const [filter, setFilter] = useState<Filter>('all');
  const [planKey, setPlanKey] = useState('');
  const [cycle, setCycle] = useState<Cycle>('semiannual');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<{ codes: string[]; planName: string; cycle: Cycle } | null>(null);

  const counts = useMemo(() => {
    const used = codes.filter((c) => c.redeemedAt).length;
    const revoked = codes.filter((c) => c.revokedAt).length;
    return { all: codes.length, used, available: codes.length - used - revoked };
  }, [codes]);

  const rows = useMemo(() => {
    if (filter === 'used') return codes.filter((c) => c.redeemedAt);
    if (filter === 'available') return codes.filter((c) => !c.redeemedAt && !c.revokedAt);
    return codes;
  }, [codes, filter]);

  const effectivePlan = planKey || plans[0]?.key || '';
  const chosenPlan = plans.find((p) => p.key === effectivePlan);

  async function copy(text: string, message: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast(message, 'success');
    } catch {
      toast(tr('Seu navegador bloqueou a cópia automática. Selecione o código e copie manualmente.'), 'info');
    }
  }

  async function generate(): Promise<void> {
    if (!chosenPlan) {
      setError(tr('Escolha um plano ativo para gerar os códigos.'));
      return;
    }
    const amount = Number(quantity);
    if (!Number.isInteger(amount) || amount < 1 || amount > 50) {
      setError(tr('Informe uma quantidade entre 1 e 50.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ codes: string[] }>('/api/admin/license-codes', {
        planKey: chosenPlan.key,
        cycle,
        quantity: amount,
        note: note.trim() || undefined
      });
      setGenerated({ codes: res.codes, planName: chosenPlan.name, cycle });
      setNote('');
      await list.reload();
      toast(tr('{count} código(s) gerado(s).', { count: res.codes.length }), 'success');
    } catch (e) {
      setError(adminErrorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminScreen
      title={tr("Licenças")}
      description={tr("Gere códigos de ativação para o cliente final e acompanhe quem já resgatou. O cliente digita o código na tela inicial do site e o plano entra em vigor na hora, sem cobrança automática.")}
    >
      <LoadOrError loading={list.loading} error={list.error} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        <Card as="section">
          <h2 className="text-xl">{tr("Gerar códigos")}</h2>
          <p className="nl-hint mt-1">
            {tr("Cada código ativa uma licença em um único estabelecimento. O formato é NL-XXXX-XXXX-XXXX e não há reaproveitamento.")}
          </p>

          <div className="mt-4 grid gap-3">
            <div>
              <label className="nl-label" htmlFor="lic-plan">{tr("Plano")}</label>
              <Select id="lic-plan" value={effectivePlan} onChange={(e) => setPlanKey(e.target.value)} disabled={busy}>
                {plans.map((p) => (
                  <option key={p.key} value={p.key}>{p.name}</option>
                ))}
              </Select>
            </div>

            <div>
              <span className="nl-label">{tr("Ciclo")}</span>
              <div className="inline-flex rounded-md border border-line p-0.5">
                {(['monthly', 'semiannual', 'yearly'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setCycle(option)}
                    disabled={busy}
                    className={`rounded-full px-3 py-1 text-sm font-semibold transition-all ${
                      cycle === option
                        ? 'bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]'
                        : 'text-muted hover:text-graphite'
                    }`}
                  >
                    {cycleLabel(option)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="nl-label" htmlFor="lic-qty">{tr("Quantidade")}</label>
              <Input
                id="lic-qty"
                type="number"
                min={1}
                max={50}
                inputMode="numeric"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                disabled={busy}
              />
              <p className="nl-hint mt-1">{tr("De 1 a 50 códigos por lote.")}</p>
            </div>

            <div>
              <label className="nl-label" htmlFor="lic-note">{tr("Observação (opcional)")}</label>
              <Textarea
                id="lic-note"
                rows={2}
                maxLength={300}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={busy}
                placeholder={tr("Ex.: lote vendido por WhatsApp em 24/10.")}
              />
            </div>

            {error ? <Alert tone="danger" title={tr("Não foi possível gerar")}>{error}</Alert> : null}

            <Button loading={busy} disabled={busy || !chosenPlan} onClick={() => void generate()}>
              {tr("Gerar códigos")}
            </Button>
            <p className="nl-hint">
              {tr("O código ativa o plano sem registrar recebimento. O controle financeiro continua em")}{' '}
              <strong>{tr("Ativações")}</strong>{tr("; registrar lá também prorroga o período, então não faça as duas coisas para a mesma venda.")}
            </p>
          </div>
        </Card>

        <div className="grid content-start gap-4">
          {generated ? (
            <Card as="section" className="border-accent">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-xl">{tr("Códigos gerados")}</h2>
                  <p className="nl-hint mt-0.5">
                    {generated.planName} · {cycleLabel(generated.cycle)}{tr(". Envie um código para cada cliente.")}
                  </p>
                </div>
                <Button variant="secondary" onClick={() => void copy(generated.codes.join('\n'), 'Códigos copiados.')}>
                  {tr("Copiar todos")}
                </Button>
              </div>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {generated.codes.map((code) => (
                  <li key={code} className="flex items-center justify-between gap-2 rounded-md border border-line bg-surface px-3 py-2">
                    <span className="font-mono text-sm font-semibold tracking-wide">{code}</span>
                    <button
                      type="button"
                      onClick={() => void copy(code, tr('Código {code} copiado.', { code }))}
                      className="rounded px-2 py-1 text-xs font-semibold text-graphite hover:bg-card"
                    >
                      {tr("Copiar")}
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {(['all', 'available', 'used'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFilter(key)}
                  className={`rounded-full border px-3 py-1 text-sm font-semibold ${
                    filter === key
                      ? 'border-transparent bg-accent text-on-accent shadow-[0_8px_16px_-10px_var(--nl-accent)]'
                      : 'border-line bg-card text-muted hover:bg-surface'
                  }`}
                >
                  {key === 'all' ? tr('Todos') : key === 'available' ? tr('Disponíveis') : tr('Ativados')}
                  {` (${counts[key]})`}
                </button>
              ))}
              <Button variant="ghost" disabled={list.loading} onClick={() => void list.reload()}>
                {list.loading ? <Spinner label="" /> : null} {tr("Atualizar")}
              </Button>
            </div>

            {rows.length === 0 && !list.loading ? (
              <EmptyState
                title={filter === 'used' ? tr('Nenhum código ativado ainda') : tr('Nenhum código nesta lista')}
                description={tr("Gere o primeiro lote ao lado. O cliente ativa o código na tela inicial do site.")}
              />
            ) : null}

            <div className="grid gap-2">
              {rows.map((item) => {
                const status = statusOf(item);
                return (
                  <div key={item.id} className="rounded-lg border border-line bg-card p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-mono text-sm font-semibold tracking-wide">{item.code}</p>
                      <div className="flex items-center gap-2">
                        <StatusPill tone={status.tone}>{status.label}</StatusPill>
                        {!item.redeemedAt && !item.revokedAt ? (
                          <button
                            type="button"
                            onClick={() => void copy(item.code, tr('Código {code} copiado.', { code: item.code }))}
                            className="rounded px-2 py-1 text-xs font-semibold text-graphite hover:bg-surface"
                          >
                            {tr("Copiar")}
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <p className="nl-hint mt-1">
                      {item.planName ?? item.planKey} · {cycleLabel(item.billingCycle)} {tr("· emitido em")}{' '}
                      {dateTimePt(item.createdAt)}
                    </p>
                    {item.redeemedAt ? (
                      <p className="mt-1 text-sm">
                        {tr("Ativado em")} {dateTimePt(item.redeemedAt)}
                        {item.redeemedBusinessName ? ` por ${item.redeemedBusinessName}` : ''}
                        {item.redeemedByName ? ` (${item.redeemedByName}${item.redeemedByEmail ? ` · ${item.redeemedByEmail}` : ''})` : ''}.
                      </p>
                    ) : null}
                    {item.note ? <p className="nl-hint mt-1">{tr("Observação:")} {item.note}</p> : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </AdminScreen>
  );
}
